import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import DeleteModal from "src/components/DeleteModal";
import Modal from "src/components/Modal";
import Title from "src/components/Title";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { removeCookie } from "src/utils/cookies";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { IRefModalsProfile, ITwoFactorAuthentication } from "./interface";
import { schemaTFA, schemaUserChangePassword } from "./schemas";
import { StyledA } from "./styles";

const ModalsProfile: React.ForwardRefRenderFunction<IRefModalsProfile> = (_, ref) => {
	const {translate} = useTranslation();
	const {addToast} = RCCHookToast();
	const {setLoading} = useLoaderBackdrop();

	const [notShowCloseModal, setNotShowCloseModal] = useState(false);
	const [isResetPassword, setResetPassword] = useState(false);

	const efa005Id = useRef(0);

	const modalChangePassword = useRef<RCCRefModalProps>(null);
	const {handleSubmit: handleSubmitChangePassword, register: registerChangePassword, formState: { errors: errorsChangePassword }, clearErrors: clearErrorsChangePassword, reset: resetChangePassword, setError: setErrorChangePassword } = useForm();

	const openModalChangePassword = (notShowCloseModalX?: boolean) => {
		setNotShowCloseModal(notShowCloseModalX ?? false);
		resetChangePassword();
		setResetPassword(false);
		modalChangePassword.current?.openModal();
	};

	const openModalResetPassword = (efa005IdX: number) => {
		setResetPassword(true);
		resetChangePassword();
		efa005Id.current = efa005IdX;
		modalChangePassword.current?.openModal();
	};

	const submitChangePassword = async (dt: any) => {
		try{
			clearErrorsChangePassword();

			if(!isResetPassword && (dt.efa005Senha_previous === dt.efa005Senha)){
				addToast({
					color: "yellow",
					description: translate("Você está inserindo a mesma senha nos campos de senha atual e nova senha"),
					title: translate("Atenção")
				});
				return;
			}

			modalChangePassword.current?.setLoadingButton();
			await schemaUserChangePassword(translate, isResetPassword).validate(dt, {
				abortEarly: false
			});

			await RCCServicesApi.postRequest({
				method: "User/ChangePassword",
				body: {
					efa005Senhaprevious: dt.efa005Senha_previous,
					efa005Senha: dt.efa005Senha,
					isResetPassword,
					efa005Id: isResetPassword ? efa005Id.current : null
				}
			});

			addToast({
				title: translate("Sucesso"),
				description: translate("Senha alterada com sucesso"),
				color: "green"
			});

			removeCookie("NeedToChangePassword");

			modalChangePassword.current?.resetLoadingButton();
			modalChangePassword.current?.closeModal();
		}catch(err){
			modalChangePassword.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorChangePassword))
				return;

			standardErrorMessages(err, translate("Erro ao alterar senha"), addToast);
		}	
	};

	const modalTFA = useRef<RCCRefModalProps>(null);
	const {handleSubmit: handleSubmitTFA, register: registerTFA, reset: resetTFA, clearErrors: clearErrorsTFA, setError: setErrorTFA, formState: {errors: errorsTFA} } = useForm();
	const [tfaData, setTfaData] = useState<ITwoFactorAuthentication>({} as ITwoFactorAuthentication);
	const {confirm} = useConfirmModal();

	const openModalTFA = async () => {
		resetTFA();
		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "TwoFactorAuthentication/GenerateTFA"
			});
			setTfaData(data);
			modalTFA.current?.openModal();
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar informações para autenticação em dois fatores"), addToast);
		}
	};
	const onSubmitTFA = async (dt: any) => {
		try{
			clearErrorsTFA();
			modalTFA.current?.setLoadingButton();
			await schemaTFA(translate).validate(dt, {
				abortEarly: false
			});

			await RCCServicesApi.postRequest({
				method: "TwoFactorAuthentication/Confirm",
				body: {
					code: dt.code,
					secret: tfaData.secret
				}
			});

			addToast({
				title: translate("Sucesso"),
				description: translate("Autenticação em dois fatores inserida com sucesso"),
				color: "green"
			});

			modalTFA.current?.resetLoadingButton();
			modalTFA.current?.closeModal();
		}catch(err){
			modalTFA.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorTFA))
				return;

			standardErrorMessages(err, translate("Erro ao gerar autenticação em dois fatores"), addToast);
		}	
	};
	const deleteTFA = async () => {
		confirm(
			translate("Exclusão de Autenticação em dois fatores"), 
			`${translate("Deseja realmente excluir a Autenticação em dois fatores")}?`,
			async () => {
				setLoading(true);
				await RCCServicesApi.deleteRequest({
					method: "TwoFactorAuthentication/Delete"
				});
				setLoading(false);
				addToast({
					title: translate("Sucesso"),
					description: translate("Autenticação em dois fatores removida com sucesso"),
					color: "green"
				});
				modalTFA.current?.closeModal();
			},
			(err) => {
				setLoading(false);
				standardErrorMessages(err, translate("Erro ao excluir autenticação em dois fatores"), addToast);
			},
			true
		);
	};

	useImperativeHandle(ref, () => ({
		openModalChangePassword,
		openModalResetPassword,
		openModalTFA
	}));

	return(
		<>
			{/* Modal Delete */}
			<DeleteModal
				ref={modalChangePassword}
				title= {translate("Alterar senha")}
				description= {translate("Para alterar a senha, escolha uma nova senha e insira-a abaixo, juntamente com sua confirmação")}
				onSubmit = {handleSubmitChangePassword(submitChangePassword)} 
				height="360px"
				hiddenCloseButton={notShowCloseModal}
				footer={{
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Confirmar"),
					cancelButtonText: translate("Cancelar"),
					showSaveButton: true
				}}
				isInfo
			>
				{!isResetPassword && (
					<RCCGrid container item >
						<RCCInput 
							name="efa005Senha_previous"
							type="password"
							register={registerChangePassword}
							label={translate("Senha atual")}
							maxLength={30}
							defaultValue=""
							error={errorsChangePassword.efa005Senha_previous}
						/>
					</RCCGrid>
				)}
				<RCCGrid container item >
					<RCCInput 
						name="efa005Senha"
						type="password"
						register={registerChangePassword}
						label={translate("Nova senha")}
						maxLength={30}
						defaultValue=""
						error={errorsChangePassword.efa005Senha}
					/>
				</RCCGrid>
				<RCCGrid container item >
					<RCCInput 
						name="efa005Senha_confirmation"
						type="password"
						register={registerChangePassword}
						label={translate("Confirmar nova senha")}
						maxLength={30}
						defaultValue=""
						error={errorsChangePassword.efa005Senha_confirmation}
					/>
				</RCCGrid>
			</DeleteModal>

			<Modal
				ref={modalTFA}
				header={{
					title: translate("Autenticação em dois fatores"),
					icon: {
						name: "shield"
					}
				}}
				footer={{
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Salvar"),
					cancelButtonText: translate("Cancelar"),
					showSaveButton: !tfaData.hasTFAAuthentication
				}}
				onSubmit={handleSubmitTFA(onSubmitTFA)}
				styleInline={tfaData.hasTFAAuthentication ? `width: 50vw;
                min-width: 300px;
                height: 220px;
                position: absolute;
                top: 60px;` : ""}
			>
				{tfaData.hasTFAAuthentication ? (
					<RCCGrid container item alignItems="center" direction="column">
						<Title color="defaultColor">{translate("Seu autenticador já está cadastrado")}</Title>
						<Title color="defaultColor">{translate("Você pode remover a autenticação em dois fatores clicando sobre o botão abaixo")}</Title>
						<RCCTooltip
							color="red"
							text={translate("Remover autenticação em dois fatores")}
							position="left"
						>
							<RCCButton
								color="red"
								icon={{
									name: "trash"
								}}
								layout="isRounded"
								size="md"
								onClick={deleteTFA}
							/>
						</RCCTooltip>
					</RCCGrid>
				): (
					<>
						<Title color="defaultColor">{translate("Para usar um aplicativo autenticador, siga as seguintes etapas")}:</Title>
						<br />
						<RCCGrid container item direction="column">
							<Title color="defaultColor">
								1. {translate("Baixe um aplicativo autenticador de dois fatores como o Microsoft Authenticator")}:
							</Title>
							<StyledA href="https://go.microsoft.com/fwlink/?Linkid=825071">Windows Phone</StyledA>
							<StyledA href="https://go.microsoft.com/fwlink/?Linkid=825072">Android</StyledA>
							<StyledA href="https://go.microsoft.com/fwlink/?Linkid=825073">iOS</StyledA> 
						</RCCGrid>
						<RCCGrid container item direction="column">
							<Title color="defaultColor">Google Authenticator:</Title>
							<StyledA href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en">Android</StyledA>
							<StyledA href="https://itunes.apple.com/us/app/google-authenticator/id388497605?mt=8">iOS</StyledA>
						</RCCGrid>
						<RCCGrid container item direction="column">
							<Title color="defaultColor">2. {translate("Digitalize o código QR ou insira esta chave")} &nbsp; <strong> {tfaData.sharedKey} </strong></Title>
							<RCCGrid container item alignItems="center">
								<img src={tfaData.authenticatorUri} alt="QR Code" />
								

							</RCCGrid>
						</RCCGrid>
						<p>
							<Title color="defaultColor">3. {translate("Depois de digitalizar o código QR ou inserir a chave acima, seu aplicativo de autenticação de dois fatores irá fornecer-lhe um código único. Digite o código na caixa de confirmação abaixo")}</Title>
						</p>
						<RCCGrid container item alignItems="center" justifyContent="center">
							<RCCGrid container item xs={12} sm={4} md={3} lg={2}>
								<RCCInput 
									register={registerTFA}
									name="code"
									label={translate("Código de verificação")}
									maxLength={6}
									required
									error={errorsTFA.code}
								/>
							</RCCGrid>
						</RCCGrid>
					</>
				)}
			</Modal>
		</>
    
	);};

export default forwardRef(ModalsProfile);
